
import { computed, defineComponent, PropType, reactive } from 'vue'

// types
import { InvoiceT, InvoiceTrip, InvoiceTtn, InvoiceCourierT } from '@/core/types/orders.types'
import { PVZGetByIDT } from '@/core/types/pvz.types'
import { CashPaymentStatusByInvoice } from '@/new-services/types/cash'

import TheButton from '@/common/components/UI/TheButton/index.vue'

// module components
import HandledValue from '@/modules/InvoiceModule/components/HandledValue/index.vue'
import ShortcomingFiles from '@/modules/InvoiceModule/components/ShortcomingFiles/index.vue'
import AdditionalServicesBlock from '@/modules/InvoiceModule/components/AdditionalServicesBlock/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import LtlInsuranceTable from '@/modules/InvoiceModule/components/LtlInsuranceTable/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

import InvoiceContentPhotos from '@/modules/InvoiceModule/components/InvoiceContentPhotos/index.vue'

// realisations
import { StatusHistoryItem } from '@/core/types/create_order.types'
import InvoiceHistoryStatusRow from '@/modules/InvoiceModule/components/InvoiceHistoryStatusRow/index.vue'
import { TRIPS_STATUSES } from '@/core/constants/tms.constants'
import { invoices } from '@/core/realisations/invoices.realisation'
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

import { formatDateFromBackend, formatDateWithTimeFromBackend } from '@/common/helpers/formatDate'

const COLUMNS = [
    {
        key: 'createdAt',
        name: 'Дата',
    },
    {
        key: 'statusName',
        name: 'Статусы',
    },
    {
        key: 'initiatorName',
        name: 'Инициатор',
    },
    {
        key: 'initiatorEmail',
        name: 'Почта инициатора',
    },
    {
        key: 'source',
        name: 'Источник',
    },
    {
        key: 'statusCode',
        name: 'Код статуса',
    },
]

export default defineComponent({
    components: {
        AdditionalServicesBlock,
        ShortcomingFiles,
        HandledValue,
        NewTable,
        LtlInsuranceTable,
        InvoiceHistoryStatusRow,
        Cheap,
        NewButton,
        Loader,
        InvoiceContentPhotos,
        TheButton,
    },
    props: {
        invoice: {
            type: Object as PropType<InvoiceT>,
            default: null,
        },
        transcriptLoading: {
            type: Boolean,
            default: false
        },
        transcriptCreated: {
            type: Boolean,
            default: false,
        },
        transcriptId: {
            type: Number,
            default: null
        },
        cashPaymentLoading: {
            type: Boolean,
            default: false,
        },
        cashPaymentFailed: {
            type: Boolean,
            default: false,  
        },
        cashPaymentItem: {
            type: [Object, null] as PropType<CashPaymentStatusByInvoice | null>,
            default: null
        },
        trip: {
            type: Object as PropType<InvoiceTrip>,
            default: null,
        },
        transit: {
            type: Array as PropType<{ ttns: InvoiceTtn[] }[]>,
            default: () => [],
        },
        pvz: {
            type: Object as PropType<PVZGetByIDT>,
            default: null,
        },
        isPvzFailed: {
            type: Boolean,
            default: false,
        },
        isPvzLoading: {
            type: Boolean,
            default: false,
        },
        isTripsLoading: {
            type: Boolean,
            default: false,
        },
        clientSlaDate: {
            type: [String, null] as PropType<string | null>,
            default: null,
        },
        currentTab: {
            type: String,
            default: 'general',
        },
        statusHistory: {
            type: Array as PropType<StatusHistoryItem[]>,
            default: () => [],
        },
        deliveryCourier: {
            type: [Object, null] as PropType<InvoiceCourierT | null>,
            default: null,
        },
        takeCourier: {
            type: [Object, null] as PropType<InvoiceCourierT | null>,
            default: null,
        },
    },
    emits: ['reload-pvz', 'shortcoming', 'delete-status'],
    setup(props, { emit }) {
        const notifications = useNotifications()
        const { havePermission } = useAccount()

        const state = reactive({
            is_loading: false,
        })

        const emitReloadPVZ = () => {
            emit('reload-pvz')
        }

        const getCostPrediction = (cost_prediction: number | null, status_code: number) => {
            if (cost_prediction === -1) {
                return 'Не указано'
            } else if (cost_prediction === null) {
                return 'Будет доступно после обработки груза'
            } else {
                if ([204, 212, 503].includes(status_code)) {
                    return ''
                } else if (status_code === 211) {
                    return cost_prediction
                } else {
                    return 'от ' + cost_prediction
                }
            }
        }

        const formatTimeRange = (input: string): string => {
            const match = input.match(/(\d{2}):(\d{2}):(\d{2})\s*-\s*(\d{2}):(\d{2}):(\d{2})/)

            if (!match) {
                throw new Error("Неверный формат времени")
            }

            const [
                ,
                sh, sm, ss,
                eh, em, es
            ] = match.map(Number)

            const startMinutes = sh * 60 + sm
            const endMinutes = eh * 60 + em + (es > 0 ? 1 : 0) + 1

            const format = (m: number) =>
                `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:${String(
                m % 60
                ).padStart(2, "0")}`

            return `${format(startMinutes)} - ${format(endMinutes)}`
        }

        //

        const isCanShowSenderServices = computed(() => {
            if (
                !props.invoice ||
                !props.invoice.order_additional_services_values ||
                !props.invoice.order_additional_services_values.length
            )
                return false
            return true
        })

        const isCanShowReceiverServices = computed(() => {
            if (
                !props.invoice ||
                !props.invoice.additional_services_values ||
                !props.invoice.additional_services_values.length
            )
                return false
            return true
        })

        const getSenderAddress = computed(() => {
            if (props.invoice && props.invoice.sender) {
                if (props.invoice.sender.self_delivery) return 'Самопривоз'
                else return props.invoice.sender.full_address
            }

            return '-'
        })

        const getTakeDate = computed(() => {
            if (props.invoice) {
                if (props.invoice.take_date) {
                    let take_date = props.invoice.take_date.split('-').reverse().join('.')

                    if (props.invoice.takeTimeSlotTime) {
                        return `${take_date} (Тайм-слот: ${formatTimeRange(props.invoice.takeTimeSlotTime)})`
                    }

                    return `${take_date} (Период: ${props.invoice.period || '-'})`
                }
            }

            return '-'
        })

        const getReceiverAddress = computed(() => {
            if (props.invoice && props.invoice.receiver) {
                if (props.invoice.receiver.is_pickup) return 'Самовывоз'
                else return props.invoice.receiver.full_address
            }

            return '-'
        })

        const isDelivered = computed(() => {
            return props.invoice && props.invoice.status && props.invoice.status.code === 211
        })
        const isCourierName = computed(() => {
            return props.invoice && props.invoice.status && props.invoice.status.code === 202
        })
        const isPretension = computed(() => {
            return props.invoice && props.invoice.pretension
        })
        const isInvoiceFiles = computed(() =>
            Boolean(props.invoice && props.invoice.invoice_files.length)
        )
        const isCertificate = computed(() =>
            Boolean(props.invoice && props.invoice.certificate_of_safety_files.length)
        )
        const getVerification = computed(() => {
            if (!props.invoice || props.invoice.verify === null) {
                return 'Нет'
            }

            if ([1, 2].includes(props.invoice.verify)) {
                return 'Да'
            } else return 'Нет'
        })
        const isParametersHandled = computed(() => {
            if (props.invoice.source_characteristics.length > 0) {
                return true
            }

            return false
        })

        const getDate = (date: string) => {
            if (!date) return '-'

            const dateArr = date.split('T')
            const dateStr = dateArr[0]
            const dateStrFormatted = dateStr.split('-').reverse().join('.')
            const timeStr = dateArr[1].split('.')[0]

            return `${dateStrFormatted} ${timeStr}`
        }

        const emitShortcoming = (isShortcoming: boolean) => {
            emit('shortcoming', isShortcoming)
        }

        const getCheapClass = (id: number) => {
            let className = 'small '
            className += TRIPS_STATUSES[id - 1]
            return className
        }

        const deleteStatus = async (id: number) => {
            const answer = confirm('Вы уверены, что хотите удалить статус?')
            if (!answer || !id) return

            try {
                state.is_loading = true

                await invoices.deleteStatus(id)

                notifications.notify({ title: 'Статус успешно удален!', type: 'success' })
                emit('delete-status')
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При удалении статуса произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const isGeneral = computed(() => props.currentTab === 'general')
        const isPayment = computed(() => props.currentTab === 'payment')
        const isSender = computed(() => props.currentTab === 'sender')
        const isReceiver = computed(() => props.currentTab === 'receiver')
        const isCargo = computed(() => props.currentTab === 'cargo')
        const isAdditional = computed(() => props.currentTab === 'additional')
        const isStatus = computed(() => props.currentTab === 'status')
        const isTransit = computed(() => props.currentTab === 'transit')
        const isTradein = computed(() => props.currentTab === 'tradein')
        const isFiles = computed(() => props.currentTab === 'files')

        const isPlaces = computed(() => {
            return props.invoice.invoice_places && props.invoice.invoice_places.length
        })
        const isDeleteStatus = computed(() => {
            return havePermission('cabinet.remove-order-status')
        })

        //

        return {
            state,
            //
            emitReloadPVZ,
            getCostPrediction,
            //
            getSenderAddress,
            getDate,
            getTakeDate,
            getReceiverAddress,
            isDelivered,
            isCourierName,
            isPretension,
            isInvoiceFiles,
            isCertificate,
            isCanShowSenderServices,
            isCanShowReceiverServices,
            getVerification,
            isParametersHandled,
            isGeneral,
            isPayment,
            isSender,
            isReceiver,
            isCargo,
            isAdditional,
            isStatus,
            isTransit,
            isTradein,
            isFiles,
            emitShortcoming,
            getCheapClass,
            isPlaces,
            COLUMNS,
            deleteStatus,
            isDeleteStatus,
            formatTimeRange,
            formatDateFromBackend,
            formatDateWithTimeFromBackend,
        }
    },
})
